/**
 * Senior Photo Upload API Client
 * 
 * Handles profile image uploads to Cloudflare R2 via backend API.
 */

import axiosClient from './axiosClient';

/**
 * Upload a profile photo for a senior
 * 
 * @param {string} seniorId - Senior profile ID
 * @param {File} file - Image file to upload (max 2MB, JPEG/PNG/WebP)
 * @returns {Promise<Object>} Response with { ok: true, photo_url: "..." }
 * @throws {Error} If upload fails
 */
export async function uploadSeniorPhoto(seniorId, file) {
  if (!seniorId) {
    throw new Error('Senior ID is required');
  }
  
  if (!file) {
    throw new Error('File is required');
  }
  
  // Validate file size (2MB max)
  const MAX_SIZE = 2 * 1024 * 1024; // 2MB
  if (file.size > MAX_SIZE) {
    throw new Error(`Datei zu groß. Maximum: ${MAX_SIZE / 1024 / 1024}MB`);
  }
  
  // Validate file type
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error('Ungültiges Dateiformat. Erlaubt: JPEG, PNG, WebP');
  }
  
  // Create FormData for multipart upload
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await axiosClient.post(
      `/api/seniors/${seniorId}/photo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Optional: Track upload progress
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(`Upload progress: ${percentCompleted}%`);
        },
      }
    );
    
    return response.data; // { ok: true, photo_url: "...", senior_id: "..." }
  } catch (error) {
    console.error('Photo upload failed:', error);
    
    // Extract error message from response
    if (error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    
    throw new Error('Foto-Upload fehlgeschlagen. Bitte erneut versuchen.');
  }
}

/**
 * Delete a senior's profile photo (future feature)
 * 
 * @param {string} seniorId - Senior profile ID
 * @returns {Promise<Object>} Response with deletion status
 */
export async function deleteSeniorPhoto(seniorId) {
  // TODO: Implement when backend endpoint is available
  throw new Error('Not implemented yet');
}

export default {
  uploadSeniorPhoto,
  deleteSeniorPhoto,
};
